// src/core/transport.ts
import {io, type Socket} from 'socket.io-client';
import {playIncomingSound} from './sound';
import {track} from './telemetry';
import {
  appendMessage,
  connectionStatus,
  currentOperator,
  messages,
  operatorTyping,
  screen,
  sessionClosed,
  unreadCount,
  updateMessage,
} from '../state/widget';
import type {ChatMessage} from '../types/public';
import type {QueuedOp} from '../types/internal';
import type {RawFilePayload, RawIncomingMessage} from '../types/events';
import {ackEventSchema} from '../types/events';
import {generateLocalId, guessFileType, mapFilePayload, mapIncomingMessage, mapType,} from './messages';
import {rewriteAbsoluteToProxy} from './url-rewrite';

/**
 * Строит widget-file URL из fileId + token + clientId через proxy apiUrl.
 * Тот же паттерн что в uploadAndEmit (proxyUrl) — единая логика для входящих/исходящих.
 */
function buildProxyFileUrl(fileId: number, opts: WidgetSocketOptions): string {
  const base = opts.apiUrl.replace(/\/+$/, '');
  return (
    `${base}/api/widget/file/${fileId}` +
    `?token=${encodeURIComponent(opts.token)}` +
    `&clientId=${encodeURIComponent(String(opts.clientId))}`
  );
}

/**
 * Резолвит final fileUrl у message:
 *  1. Если есть absolute URL (от backend) — rewrite internal cp.o.kg на Gravitee proxy.
 *  2. Если URL нет, но есть fileId (typical для credentials-set history без enrich'а) —
 *     строим URL сами из apiUrl+token+clientId.
 *  3. Иначе как есть.
 *
 * См. core/url-rewrite.ts для rationale rewrite'а.
 */
function withProxyFileUrl(msg: ChatMessage): ChatMessage {
  if (!lastOptions) return msg;
  if (msg.fileUrl) {
    const fixed = rewriteAbsoluteToProxy(msg.fileUrl, lastOptions.apiUrl);
    return fixed === msg.fileUrl ? msg : { ...msg, fileUrl: fixed };
  }
  if (msg.fileId != null && Number.isFinite(msg.fileId)) {
    return { ...msg, fileUrl: buildProxyFileUrl(msg.fileId, lastOptions) };
  }
  return msg;
}

export type WidgetSocketOptions = {
  apiUrl: string;
  wsPath?: string;
  token: string;
  clientId: number;
  username: string;
};

let socket: Socket | null = null;
let lastOptions: WidgetSocketOptions | null = null;

const pendingAcks = new Map<string, ReturnType<typeof setTimeout>>();
const ACK_TIMEOUT_MS = 10_000;
// Сколько ждём после disconnect прежде чем показать "offline" баннер.
// Ранее 30s — слишком долго, user не понимает что соединение потеряно во время
// перезагрузки cp-core. socket.io сам пытается reconnect'нуться бесконечно;
// этот порог влияет только на UX-индикатор "offline vs reconnecting".

const outboundQueue: QueuedOp[] = [];
let disconnectedSince: number | null = null;
const OFFLINE_THRESHOLD_MS = 8_000;

let offlineCheckTimer: ReturnType<typeof setTimeout> | null = null;
let typingTimer: ReturnType<typeof setTimeout> | null = null;

// Удерживаем оригинальный File до успешного HTTP-upload'а —
// retry для file-сообщений без fileId должен уметь повторить загрузку.
// Не персистим в localStorage (File не JSON-serializable, blob-URL после reload мёртв).
const pendingFiles = new Map<string, { file: File; mime: string }>();

function scheduleAckTimeout(clientExternalId: string): void {
  if (pendingAcks.has(clientExternalId)) {
    clearTimeout(pendingAcks.get(clientExternalId));
  }
  const timer = setTimeout(() => {
    pendingAcks.delete(clientExternalId);
    const cur = messages.value.find(
      (m) => (m.clientExternalId ?? m.id) === clientExternalId,
    );
    if (cur && (cur.status === 'sending' || cur.status === 'sent')) {
      updateMessage(clientExternalId, { status: 'failed' });
    }
  }, ACK_TIMEOUT_MS);
  pendingAcks.set(clientExternalId, timer);
}

function scheduleOfflineCheck(): void {
  if (offlineCheckTimer) return;
  offlineCheckTimer = setTimeout(() => {
    offlineCheckTimer = null;
    if (
      connectionStatus.value === 'reconnecting' &&
      disconnectedSince !== null &&
      Date.now() - disconnectedSince >= OFFLINE_THRESHOLD_MS
    ) {
      connectionStatus.value = 'offline';
      for (const op of outboundQueue) {
        updateMessage(op.clientExternalId, { status: 'failed' });
      }
      outboundQueue.length = 0;
    }
  }, OFFLINE_THRESHOLD_MS);
}

function flushOutboundQueue(): void {
  while (outboundQueue.length > 0) {
    if (!socket || !socket.connected) return;
    const op = outboundQueue.shift();
    if (!op) break;
    if (op.kind === 'text') {
      socket.emit('newMessage', { text: op.text, clientExternalId: op.clientExternalId });
      scheduleAckTimeout(op.clientExternalId);
    } else if (op.kind === 'file') {
      socket.emit('newFile', {
        fileId: op.fileId,
        clientExternalId: op.clientExternalId,
        name: op.name,
        type: op.type,
      });
      scheduleAckTimeout(op.clientExternalId);
    }
  }
}

/**
 * WS-маршрут идёт через nginx напрямую (не через Gravitee gateway), поэтому
 * `path` НЕ конкатенируется с pathname от apiUrl.
 *
 * Дефолт `/wc/` совпадает с prod cp-core `WEB_WS_PATH=/wc/` (унаследовано
 * от legacy mpwc, который на проде ходит на `wss://ccp.o.kg/wc/`).
 * На staging cp-core слушает `/wcs/`, поэтому build:staging выставляет
 * `VITE_PUBLIC_WS_PATH=/wcs/`.
 *
 * Если у канала в `params.widget.ws_path` задан override — используется он
 * (см. `connect()` ниже, опция `wsPath`).
 *
 * apiUrl используется только для определения origin (см. `apiOrigin()`),
 * но не для path WS-handshake.
 */
function deriveWsPath(_apiUrl: string): string {
  const raw = import.meta.env?.VITE_PUBLIC_WS_PATH;
  const suffix = typeof raw === 'string' && raw.length > 0 ? raw : '/wc/';
  return suffix.startsWith('/') ? suffix : `/${suffix}`;
}

function apiOrigin(apiUrl: string): string {
  try {
    return new URL(apiUrl).origin;
  } catch {
    return apiUrl;
  }
}

export function connect(opts: WidgetSocketOptions): Socket {
  if (socket) return socket;
  lastOptions = opts;

  connectionStatus.value = 'connecting';
  const s = io(apiOrigin(opts.apiUrl), {
    path: opts.wsPath ?? deriveWsPath(opts.apiUrl),
    // websocket первично, polling — как fallback при проблемах
    // (restrictive corporate networks, проблемы с WS-апгрейдом, перезагрузка бэка).
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 5_000,
    // Рандомизация задержки помогает избегать thundering herd при старте cp-core.
    randomizationFactor: 0.5,
    // Быстрее понимаем что соединение "зависло" (стандартное ping-поведение).
    timeout: 10_000,
  });

  s.on('connect', () => {
    connectionStatus.value = 'online';
    disconnectedSince = null;
    s.emit('credentials', {
      clientId: opts.clientId,
      splitToken: opts.token,
      username: opts.username,
      messageId: null,
    });
    flushOutboundQueue();
  });

  s.on('disconnect', () => {
    connectionStatus.value = 'reconnecting';
    if (disconnectedSince === null) disconnectedSince = Date.now();
    scheduleOfflineCheck();
  });

  s.on('connect_error', () => {
    connectionStatus.value = 'reconnecting';
    if (disconnectedSince === null) disconnectedSince = Date.now();
    scheduleOfflineCheck();
  });

  s.on('ack', (raw: unknown) => {
    const parsed = ackEventSchema.safeParse(raw);
    if (!parsed.success) return;
    const ack = parsed.data;
    const target = ack.clientExternalId;
    const timer = pendingAcks.get(target);
    if (timer) {
      clearTimeout(timer);
      pendingAcks.delete(target);
    }
    const patch: Partial<ChatMessage> = {};
    if (ack.status === 'accepted') patch.status = 'sent';
    if (ack.status === 'delivered') patch.status = 'delivered';
    if (ack.status === 'read') patch.status = 'read';
    if (ack.externalId) patch.externalId = ack.externalId;
    updateMessage(target, patch);
  });

  s.on('credentials-set', (raw: unknown) => {
    // Backend теперь шлёт полную историю сессии на каждый connect.
    // Поддерживаем два shape'а:
    //   1) legacy: просто массив сообщений
    //   2) v2: { messages: [...], session?, operator? }
    // В обоих случаях полностью заменяем messages.value — источник истины это backend,
    // локальная копия больше не персистится.
    const isV2 = raw && typeof raw === 'object' && !Array.isArray(raw) && 'messages' in raw;
    const rawMessages: unknown[] = isV2
      ? (Array.isArray((raw as { messages: unknown }).messages)
          ? ((raw as { messages: unknown[] }).messages)
          : [])
      : Array.isArray(raw)
        ? (raw as unknown[])
        : [];
    const restored = rawMessages
      .map((m) => mapIncomingMessage(m as RawIncomingMessage))
      .filter((m): m is ChatMessage => m !== null)
      .map(withProxyFileUrl);
    messages.value = restored;
  });

  s.on('message', (raw: RawIncomingMessage) => {
    const mapped = mapIncomingMessage(raw);
    if (!mapped) return;
    const msg = withProxyFileUrl(mapped);
    appendMessage(msg);
    if (msg.author && msg.direction === 'in') {
      currentOperator.value = msg.author;
    }
    operatorTyping.value = false;
    if (typingTimer !== null) {
      clearTimeout(typingTimer);
      typingTimer = null;
    }
    if (msg.direction === 'in') {
      // throttled: при спаме оператора звук не повторяется чаще раз в 1.5 секунды.
      playIncomingSound();
      if (screen.value !== 'chat') {
        unreadCount.value = unreadCount.value + 1;
      }
    }
    if (msg.externalId) {
      s.emit('received', { messageId: msg.externalId });
    }
  });

  s.on('file', (raw: RawFilePayload) => {
    if (!raw) return;
    const m = withProxyFileUrl(mapFilePayload(raw));
    appendMessage(m);
    operatorTyping.value = false;
    if (typingTimer !== null) {
      clearTimeout(typingTimer);
      typingTimer = null;
    }
    if (m.direction === 'in') {
      playIncomingSound();
      if (screen.value !== 'chat') {
        unreadCount.value = unreadCount.value + 1;
      }
    }
  });

  s.on('sessionAction', (action: string) => {
    if (action === 'typing') {
      operatorTyping.value = true;
      if (typingTimer !== null) clearTimeout(typingTimer);
      // 6_000 > 5_000 = период cron'а cp-core (typing-event-sender.job.ts).
      // Это даёт запас, чтобы индикатор не «мигал» между тиками cron'а пока бот думает.
      typingTimer = setTimeout(() => {
        operatorTyping.value = false;
        typingTimer = null;
      }, 6_000);
      return;
    }
    if (action === 'session_closed') {
      if (sessionClosed.value) return;
      sessionClosed.value = true;
      if (typingTimer !== null) {
        clearTimeout(typingTimer);
        typingTimer = null;
      }
      operatorTyping.value = false;
      currentOperator.value = null;
      appendMessage({
        id: `system-closed-${Date.now()}`,
        direction: 'in',
        type: 'system',
        systemKind: 'session_closed',
        sentAt: Date.now(),
        status: 'delivered',
      });
      if (screen.value !== 'chat') {
        unreadCount.value = unreadCount.value + 1;
      }
    }
  });

  s.on(
    'pongMessage',
    (payload: {
      clientExternalId?: string;
      externalId?: string;
      fileId?: number | string;
      name?: string;
      type?: string;
    }) => {
      const target = payload?.clientExternalId ?? payload?.externalId;
      if (!target) return;
      // Статусом отправки сейчас рулит только событие `ack` от бэка:
      // - accepted  → 1 галочка (sent)     — сервер принял сообщение
      // - delivered → 2 галочки (delivered) — Message создан в БД
      // - read      → прочитано оператором
      // pongMessage — легаси-эхо, из него забираем только metadata файла.
      const patch: Partial<ChatMessage> = {
        externalId: payload.externalId ?? target,
      };
      if (payload.fileId != null) patch.fileId = Number(payload.fileId);
      if (payload.name) patch.fileName = payload.name;
      if (payload.type) patch.type = mapType(payload.type);
      updateMessage(target, patch);
    },
  );

  socket = s;
  return s;
}

export function sendText(
  text: string,
  opts: { allowWhenClosed?: boolean } = {},
): ChatMessage | null {
  // allowWhenClosed — ответ кнопкой на вопрос, пришедший после закрытия
  // диалога (опрос CSI). cp-core на время опроса сам переоткрывает сессию,
  // поэтому ответ попадёт в неё, а не создаст новую.
  if (sessionClosed.value && !opts.allowWhenClosed) return null;
  const externalId = generateLocalId();
  const pending: ChatMessage = {
    id: externalId,
    clientExternalId: externalId,
    direction: 'out',
    type: 'text',
    text,
    sentAt: Date.now(),
    status: 'sending',
  };
  appendMessage(pending);

  if (!socket || !socket.connected) {
    outboundQueue.push({
      kind: 'text',
      text,
      clientExternalId: externalId,
      queuedAt: Date.now(),
    });
    return pending;
  }

  socket.emit('newMessage', { text, clientExternalId: externalId });
  scheduleAckTimeout(externalId);
  return pending;
}

export async function sendFile(file: File): Promise<ChatMessage | null> {
  if (sessionClosed.value) return null;
  if (!lastOptions) return null;

  const externalId = generateLocalId();
  const mime = file.type || 'application/octet-stream';
  // fileUrl намеренно не ставим: blob: URL живёт только в runtime, при reload
  // мёртв, и приходится stripO'ить на load. Вместо этого пользователь видит
  // file-bubble с именем и размером на ~1 сек, пока uploadAndEmit не вернётся
  // с реальным proxy URL.
  const pending: ChatMessage = {
    id: externalId,
    clientExternalId: externalId,
    direction: 'out',
    type: guessFileType(file.type),
    fileName: file.name,
    fileSize: file.size,
    sentAt: Date.now(),
    status: 'sending',
  };
  appendMessage(pending);
  pendingFiles.set(externalId, { file, mime });

  await uploadAndEmit(externalId, file, mime);
  return pending;
}

/**
 * HTTP multipart upload + (при успехе) WS newFile emit.
 * Изолирует flow, чтобы retryMessage мог его переиспользовать для
 * file-сообщений с потерянным fileId (HTTP upload изначально провалился).
 */
async function uploadAndEmit(
  externalId: string,
  file: File,
  mime: string,
): Promise<void> {
  if (!lastOptions) {
    updateMessage(externalId, { status: 'failed' });
    return;
  }

  try {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const url = new URL(`${lastOptions.apiUrl}/api/widget/upload`);
    url.searchParams.set('token', lastOptions.token);
    url.searchParams.set('clientId', String(lastOptions.clientId));
    url.searchParams.set('clientExternalId', externalId);

    const res = await fetch(url.toString(), { method: 'POST', body: fd });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      // eslint-disable-next-line no-console
      console.warn(
        `[cpw] upload failed: HTTP ${res.status} ${res.statusText} — ${body.slice(0, 500)}`,
      );
      track('error', { stage: 'upload_http', status: res.status, body: body.slice(0, 200) });
      updateMessage(externalId, { status: 'failed' });
      return;
    }
    const json = (await res.json()) as { fileId: number; url?: string };
    // Строим proxy URL на клиенте: переживёт reload (в отличие от optimistic
    // blob:<…> который мёртв после перезагрузки страницы). Backend может
    // вернуть `url` в ответе — предпочитаем его; иначе собираем сами из
    // token + clientId (которые у нас есть в options).
    const proxyUrl = json.url ?? buildProxyFileUrl(json.fileId, lastOptions);
    // eslint-disable-next-line no-console
    console.debug('[cpw] upload complete:', { externalId, fileId: json.fileId, proxyUrl });
    updateMessage(externalId, {
      fileId: json.fileId,
      fileUrl: proxyUrl,
    });
    pendingFiles.delete(externalId);

    if (socket && socket.connected) {
      socket.emit('newFile', {
        fileId: json.fileId,
        clientExternalId: externalId,
        name: file.name,
        type: mime,
      });
      scheduleAckTimeout(externalId);
    } else {
      outboundQueue.push({
        kind: 'file',
        fileId: json.fileId,
        name: file.name,
        type: mime,
        clientExternalId: externalId,
        queuedAt: Date.now(),
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[cpw] upload threw: ${err instanceof Error ? err.message : String(err)}`);
    track('error', {
      stage: 'upload_network',
      message: err instanceof Error ? err.message : String(err),
    });
    updateMessage(externalId, { status: 'failed' });
  }
}

export function retryMessage(clientExternalId: string): void {
  const msg = messages.value.find(
    (m) => (m.clientExternalId ?? m.id) === clientExternalId,
  );
  if (!msg || msg.status !== 'failed') return;
  updateMessage(clientExternalId, { status: 'sending' });

  // Text message retry — просто re-emit newMessage.
  if (msg.type === 'text' && msg.text) {
    if (socket && socket.connected) {
      socket.emit('newMessage', { text: msg.text, clientExternalId });
      scheduleAckTimeout(clientExternalId);
    } else {
      outboundQueue.push({
        kind: 'text',
        text: msg.text,
        clientExternalId,
        queuedAt: Date.now(),
      });
    }
    return;
  }

  // File retry — разные пути в зависимости от того, где провалилась изначальная отправка.
  // Случай 1: HTTP upload прошёл, fileId известен → повторяем WS newFile.
  if (msg.fileId) {
    const pendingRef = pendingFiles.get(clientExternalId);
    const mime = pendingRef?.mime ?? msg.fileName?.split('.').pop()
      ? guessMimeFromExt(msg.fileName)
      : 'application/octet-stream';
    if (socket && socket.connected) {
      socket.emit('newFile', {
        fileId: msg.fileId,
        clientExternalId,
        name: msg.fileName ?? 'file',
        type: mime,
      });
      scheduleAckTimeout(clientExternalId);
    } else {
      outboundQueue.push({
        kind: 'file',
        fileId: msg.fileId,
        name: msg.fileName ?? 'file',
        type: mime,
        clientExternalId,
        queuedAt: Date.now(),
      });
    }
    return;
  }

  // Случай 2: HTTP upload изначально провалился (нет fileId). Повторяем загрузку,
  // если оригинальный File всё ещё в pendingFiles (живёт в памяти текущей сессии).
  const pendingRef = pendingFiles.get(clientExternalId);
  if (pendingRef) {
    void uploadAndEmit(clientExternalId, pendingRef.file, pendingRef.mime);
    return;
  }

  // Случай 3: File потерян (reload страницы или внутренний баг). Retry невозможен —
  // помечаем как failed обратно, чтобы UI не завис в 'sending'.
  // eslint-disable-next-line no-console
  console.warn(
    `[cpw] retry невозможен: оригинальный File потерян (clientExternalId=${clientExternalId}). ` +
      'Скорее всего был reload страницы после failed upload.',
  );
  updateMessage(clientExternalId, { status: 'failed' });
}

function guessMimeFromExt(fileName: string | undefined): string {
  if (!fileName) return 'application/octet-stream';
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  const map: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
    txt: 'text/plain',
    zip: 'application/zip',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    mp3: 'audio/mpeg',
    mp4: 'video/mp4',
  };
  return map[ext] ?? 'application/octet-stream';
}

export async function fetchHistoryPage(opts: {
  apiUrl: string;
  token: string;
  clientId: number;
  sinceExternalId?: string;
  untilExternalId?: string;
  limit?: number;
}): Promise<{ messages: ChatMessage[]; hasMore: boolean } | null> {
  try {
    const params = new URLSearchParams({ token: opts.token });
    if (opts.sinceExternalId) params.set('sinceExternalId', opts.sinceExternalId);
    if (opts.untilExternalId) params.set('untilExternalId', opts.untilExternalId);
    if (opts.limit) params.set('limit', String(opts.limit));
    const res = await fetch(
      `${opts.apiUrl}/api/widget/history/${opts.clientId}?${params.toString()}`,
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { messages: RawIncomingMessage[]; hasMore: boolean };
    const mapped = json.messages
      .map((m) => mapIncomingMessage(m))
      .filter((m): m is ChatMessage => m !== null);
    return { messages: mapped, hasMore: Boolean(json.hasMore) };
  } catch {
    return null;
  }
}

export function disconnect(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  for (const timer of pendingAcks.values()) clearTimeout(timer);
  pendingAcks.clear();
  if (offlineCheckTimer) {
    clearTimeout(offlineCheckTimer);
    offlineCheckTimer = null;
  }
  if (typingTimer !== null) {
    clearTimeout(typingTimer);
    typingTimer = null;
  }
  outboundQueue.length = 0;
  disconnectedSince = null;
  connectionStatus.value = 'idle';
}

export function retry(): void {
  if (!lastOptions) return;
  disconnect();
  connect(lastOptions);
}
